<script setup lang="ts">
import type { operations } from "#shopware";
import { onClickOutside } from "@vueuse/core";
import { getCmsTranslate } from "@shopware/helpers";
import { defu } from "defu";

const props = defineProps<{
  quantity: number;
}>();

const emits = defineEmits<{
  (e: "close"): void;
  (e: "openQuotation"): void;
}>();

const { quantity } = toRefs(props);

const configurationPopup = ref(null);

onClickOutside(configurationPopup, () => emits("close"));

const { apiClient } = useShopwareContext();

const { product } = useProduct();
const productId = product?.value?.id;

const result = await apiClient.invoke(
  "readProductCrossSellings post /product/{productId}/cross-selling",
  {
    headers: {
      "sw-include-seo-urls": true,
    },
    pathParams: { productId },
  }
);
const { data: crossSellingsResponse } = result;

const { unitPrice } = useProductPrice(product);
const { addProducts } = useCart();
const isAddToCartProcessing = ref(false);
const { getErrorsCodes } = useCartNotification();
const { resolveCartError } = useCartErrorParamsResolver();
const { pushSuccess, pushError } = useNotifications();
const { t } = useI18n();

const {
  selectedProducts,
  additionalPrice,
  totalPrice,
  addProduct,
  removeItem,
  clear,
} = useCustomProductConfigurator(quantity);

const crossSellings = computed(() => {
  return crossSellingsResponse?.filter(
    (crossSellings) =>
      !["Ähnliche Produkte", "D'autre recommendations"].includes(
        crossSellings.crossSelling?.translated?.name
      )
  );
});

const priceHidden = computed(
  () =>
    product.value?.translated?.customFields?.custom_product_fields_hide_price
);

type Translations = {
  errors: {
    [key: string]: string;
  };
};

let translations: Translations = {
  errors: {
    "product-stock-reached":
      "The product {name} is only available {quantity} times",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const addToCartProxy = async () => {
  isAddToCartProcessing.value = true;
  await addProducts(
    selectedProducts.value as operations["addLineItem post /checkout/cart/line-item"]["body"]["items"]
  );
  const errors = getErrorsCodes();
  errors?.forEach((element) => {
    const { messageKey, params } = resolveCartError(element);
    pushError(getCmsTranslate(translations.errors[messageKey], params));
    isAddToCartProcessing.value = false;
  });

  if (!errors.length)
    pushSuccess(
      `${product?.value?.translated.name}` +
        " " +
        t("productDetails.messages.addedToCart")
    );
  isAddToCartProcessing.value = false;
  clear();
  emits("close");
};

const openQuatation = () => {
  emits("close");
  emits("openQuotation");
};

onMounted(() => {
  addProduct({
    id: product.value?.id,
    quantity: quantity.value,
    cover: product.value?.cover?.media,
    name: product.value?.translated?.name,
    unitPrice: unitPrice.value,
    productNumber: product.value?.productNumber,
    type: "product",
  });
});
</script>
<template>
  <div
    id="ConfiguratorModal"
    class="[&:not(.show)]:opacity-0 w-full h-full fixed top-0 left-0 z-[100] overflow-y-auto show"
  >
    <div
      class="w-full h-full fixed top-0 left-0 bg-skin-primary opacity-40 -z-[1]"
    ></div>
    <div class="popup-dialog flex items-center justify-center my-10">
      <div
        ref="configurationPopup"
        class="popup-content w-full md:w-[75.625rem] py-10 px-5 md:p-10 bg-skin-white rounded relative"
      >
        <div class="popup-header mb-5 flex items-center">
          <h3 class="font-bold text-2xl">
            {{ $t("product.configurator.label") }}
          </h3>
          <button
            type="button"
            class="popup-close w-6 h-6 ml-auto bg-no-repeat bg-[length:100%] bg-humburger-close"
            @click="$emit('close')"
          ></button>
        </div>
        <div class="popup-body py-5">
          <template v-for="crossSelling in crossSellings">
            <ProductDetailCrossSelling
              :cross-selling="crossSelling"
              :quantity="quantity"
              @select="addProduct"
              @remove="removeItem"
            />
          </template>
        </div>
        <div class="popup-footer flex gap-y-10 flex-wrap items-center mt-5">
          <div class="w-full lg:w-2/4 lg:pr-4">
            <table
              class="prose-td:py-1.5 mobile:text-sm md:[&>tbody>tr>td:nth-child(2)]:text-end mobile:[&>tbody>tr>td:nth-child(2)]:whitespace-nowrap mobile:[&>tbody>tr>td:nth-child(2)]:text-end laptop:w-[28.125rem] w-full"
            >
              <tbody v-if="!priceHidden">
                <tr>
                  <td>{{ $t("product.configurator.additional") }}</td>
                  <td>
                    <b><SharedPrice :value="additionalPrice" /></b>
                  </td>
                </tr>
                <tr class="text-base laptop:text-2xl">
                  <td class="text-base">
                    <b>{{ $t("product.configurator.total") }}</b>
                  </td>
                  <td class="text-skin-theme">
                    <b><SharedPrice :value="totalPrice" /></b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="w-full lg:w-2/4">
            <div
              class="flex text-center gap-5 flex-col md:flex-row lg:flex-col justify-center laptop:justify-end laptop:flex-row mobile:prose-a:w-full mobile:prose-a:p-2.5"
            >
              <div class="flex justify-center gap-x-5">
                <button
                  @click="$emit('close')"
                  class="btn-outline cursor-pointer"
                >
                  {{ $t("product.configurator.cancel") }}
                </button>
                <button
                  v-if="priceHidden"
                  class="btn-outline btn-offertanfrage"
                  @click="openQuatation"
                >
                  {{ $t("product.quotation.label") }}
                </button>
              </div>
              <div v-if="!priceHidden">
                <button @click="addToCartProxy" class="btn h-12 flex">
                  {{ $t("product.configurator.addToCart") }}
                  <span class="ml-2">
                    <i
                      v-if="isAddToCartProcessing"
                      class="fa-solid fa-spinner animate-spin"
                    ></i>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
